import axios from 'axios'
import { motion } from 'framer-motion'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import { ColumnsTable, IAccountPayable } from 'safe'
import AdvancedAccountReceivable from '../../components/Display/AdvancedAccountReceivable'
import FloatingForm from '../../components/Display/FloatingForm'
import Modal from '../../components/Display/Modal'
import Table from '../../components/Display/Table'
import { ColumnItemWrapper } from '../../components/General/ColumnItemWrapper'
import { HeaderProps } from '../../components/Layout/Header'
import useSnackBars from '../../hooks/SnackbarConsumerHook'
import { useLocalStorageState } from '../../hooks/useLocalStorageState'
import { formatPipe, getDenomination, getYearMonthAndDay } from '../../utils/utils'
import AddPaymentForm from './AddPaymentForm'
import { useSelector } from 'react-redux'
import { RootState } from '../../redux/store'

const server = process.env.REACT_APP_SERVER_URL
const BillToPay: React.FC<IBillToPay> = ({ setHeaderActions }) => {
  
  const [errorMessage, setErrorMessage] = useState<string>('')
  
  const columns: ColumnsTable[] = [
    {
      title: 'Fecha de inicio',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (id: number, record: any) => (
        <ColumnItemWrapper
          subElement="paragraph"
          subElementStyle={'dark:text-white text-black'}
        >
          {record?.startDate.replace(/-/g, '/')}
        </ColumnItemWrapper>
      ),
    },
    {
      title: 'Acreditado',
      dataIndex: 'provider.businessName',
      key: 'provider.businessName',
      render: (id: number, record: any) => (
        <ColumnItemWrapper>
          {record?.provider?.businessName || record?.client?.name}
        </ColumnItemWrapper>
      ),
    },
    {
      title: 'Monto total',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (id: number, record: any) => (
        <ColumnItemWrapper>{`${getDenomination(record?.currency?.id)} ${formatPipe(record?.totalAmount)}` }</ColumnItemWrapper>
      ),
      // render: (id: number, record: any) => (
      //   <ColumnItemWrapper>{formatPipe(record?.totalAmount)}</ColumnItemWrapper>
      // ),
    },
    {
      title: 'Monto pagado',
      dataIndex: 'amountPaid',
      key: 'amountPaid',
      render: (id: number, record: any) => (
        <ColumnItemWrapper>{`${getDenomination(record?.currency?.id)} ${formatPipe(record?.amountPaid)}` }</ColumnItemWrapper>
      ),
      // render: (id: number, record: any) => (
      //   <ColumnItemWrapper>{formatPipe(record?.amountPaid)}</ColumnItemWrapper>
      // ),
    },
    {
      title: 'Fecha final de pago',
      dataIndex: 'finalDate',
      key: 'finalDate',
      render: (id: number, record: any) => (
        <ColumnItemWrapper
          subElement="paragraph"
          subElementStyle={'dark:text-white text-black'}
        >
          {record?.finalDate.replace(/-/g, '/')}
        </ColumnItemWrapper>
      ),
    },
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
    },
  ]

  const [accountPayablesInfo, setAccountPayablesInfo] = useState<{
    accountPayables: IAccountPayable[]
    loading: boolean
    numberOfPages: number
  }>({
    accountPayables: [],
    loading: false,
    numberOfPages: 0,
  })

  const [currentPage, setCurrentPage] = useState<number>(0)

  const [searchParams, setSearchParams] = useState<string[]>([])

  const [modalSearch, setModalSearch] = useState<boolean>(false)

  const [userConfig] = useLocalStorageState('user-config')

  const [accountPayableToAdd, setAccountPayableToAdd] = useState<any[]>([])

  const { accountPayables, loading, numberOfPages } = accountPayablesInfo

  const [addPaymentModal, setAddPaymentModal] = useState<boolean>(false)

  const { addAlert } = useSnackBars()

  const [fastSearch, setFastSearch] = useState<string>('')

  const userRole = useSelector(
    (state: RootState) => state.auth.user['custom:user_role']
  )

  const getAccountPayable = async (
    params: string[] = searchParams,
    pageNumber: number = 0,
    fastParam = fastSearch
  ): Promise<void> => {
    const paginationParams = [
      `pageNumber=${pageNumber + 1}`,
      `pageSize=${userConfig.rowsPerPage}`,
    ]
    setSearchParams(params)
    setAccountPayablesInfo((prevState) => ({
      ...prevState,
      loading: true,
    }))

    setFastSearch(fastParam)

    let newParams = [...params]

    if (fastParam) {
      newParams.push(`fastSearch=${fastParam}`)
    }

    let response = await axios.get<{
      accountPayables: IAccountPayable[]
      numberOfPages: string
    }>(
      `${server}/account-payable?${[...newParams, ...paginationParams].join('&')}`
    )
    setAccountPayablesInfo((prevState) => ({
      ...prevState,
      accountPayables: response?.data?.accountPayables,
      loading: false,
      numberOfPages: +response.data.numberOfPages || 1,
    }))
  }

  useEffect(() => {
    getAccountPayable()
  }, [])

  const onFilterHeaders = (options) => {
    if (accountPayableToAdd.length === 0) {
      const { onCreatePaymentOrder, ...rest } = options
      return rest
    }
    return options
  }

  useEffect(() => {
    setHeaderActions(onFilterHeaders({
      advancedSearch: async (value) => {
        if (typeof value === 'boolean') {
          setModalSearch(value)
          return
        }
        // setSearchParams([`fastSearch=${value}`])
        getAccountPayable(searchParams, currentPage, value)
      },
      cleanFilters: {
        sort: () => {},
        search: async () => {
          // setSearchParams([])
          getAccountPayable([], 0)
          // getAccountReceivables([], 0)
        },
        isSearch: Boolean(searchParams.length > 0),
      },
      onCreatePaymentOrder: () => {
        let first : any = accountPayableToAdd[0]
        
        let checkInvalid = accountPayableToAdd.some(accountReceivable => accountReceivable.type === 'PURCHASE' || accountReceivable.type === 'PURCHASE_REFUND')

        if (checkInvalid) {
          // addAlert({
          //   message: 'La cuentas por pagar vinculadas a compras deben de pagarse desde el módulo de compras',
          //   status: 'error',
          // })
          setErrorMessage('La cuentas por pagar vinculadas a compras deben de pagarse desde el módulo de compras')
          return
        }

        if (first?.client?.id) {
          let checkClient = accountPayableToAdd.every(item => item?.client?.id === first?.client?.id)

          if (!checkClient) {
            // addAlert({
            //   message: 'La cuenta por pagar debe de ser del mismo cliente o proveedor',
            //   status: 'error',
            // })
            setErrorMessage('La cuenta por pagar debe de ser del mismo cliente o proveedor')
            return
          }

        } else {
          let checkClient = accountPayableToAdd.every(item => item?.provider?.id === first?.provider?.id)
          if (!checkClient) {
            // addAlert({
            //   message: 'La cuenta por pagar debe de ser del mismo cliente o proveedor',
            //   status: 'error',
            // })
            setErrorMessage('La cuenta por pagar debe de ser del mismo cliente o proveedor')
            return
          }
        }
        setAddPaymentModal(true)
      }
    }))
    return () => setHeaderActions(null)
  }, [searchParams, accountPayableToAdd])

  const onPostIncomeFromAccountPayable = async (values) => {
    let body = {
      ...values,
      type: "INSTALLMENT",
      amount: values.amount+"",
      accountPayables: accountPayableToAdd.map(v => ({id: v.id}))
    }
    if (accountPayableToAdd[0]?.client?.id) {
      body = {
        ...body,
        beneficiary: {
          client: {
            id: accountPayableToAdd[0]?.client?.id
          }
        }
      }
    }  else {
      body = {
        ...body,
        beneficiary: {
          provider: {
            id: accountPayableToAdd[0]?.provider?.id
          }
        }
      }
    }

    try {
      addAlert({
        message: 'Realizando pago...',
        status: 'loading',
      })
      await axios.post(`${server}/outflow`, body)
      await getAccountPayable([], 0)
      setAccountPayableToAdd([])
      addAlert({
        message: 'Pago realizado satisfactoriamente',
        status: 'success',
      })
    } catch (err) {
      addAlert({
        message: 'Error al realizar pago',
        status: 'error',
      })
    } finally {
      setAddPaymentModal(false)
    }

  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <Table
        data={accountPayables}
        columns={columns}
        pages={numberOfPages}
        pageSelected={currentPage}
        loading={loading}
        // onPressButton={pressRow}
        setPageSelected={setCurrentPage}
        isArraySelectable={userRole === "seniat" ? false : true}
        selectedData={accountPayableToAdd}
          setSelectedData={(data) => {
            setAccountPayableToAdd(data)
            // setBillsToPayToAdd(data)
          }}
        // setLoading={setLoading}
      />

      <FloatingForm
        visible={modalSearch}
        onClose={() => setModalSearch(false)}
        topClass="top-20"
        rightClass="right-10"
        containerClass="w-2/6"
      >
        <AdvancedAccountReceivable
          onClose={() => setModalSearch(false)}
          onSubmit={async (values) => {
            console.log("values..", values)
            if (values.isDateSelected) {
              const [yearFrom, monthFrom, dayFrom] = getYearMonthAndDay(
                values.fromDate
              )
              const [yearTo, monthTo, dayTo] = getYearMonthAndDay(values.toDate)
              let from = new Date(yearFrom, monthFrom, dayFrom)
              let to = new Date(yearTo, monthTo, dayTo + 1)
  
              let dateFrom = DateTime.fromISO(from.toISOString()).toISODate()
              let dateTo = DateTime.fromISO(to.toISOString()).toISODate()
              // console.log('date prueba...', datePrueba)
  
              // let newTo = to.toISOString()
              // console.log("NEW", newTo.replace(/T.+/, '').replace(/\-/g, "/"))
              // console.log("TO", to.toLocaleDateString('en-US'))
              // let [year, month, day] = newTo.replace(/T.+/, '').replace(/\-/g, "/").split("/")
              // const [yearToNew, monthToNew, dayToNew] = getYearMonthAndDay(`${day}/${month}/${year}`)
  
              values.fromDate = dateFrom
  
              values.toDate = dateTo
            }

            if (!values.isDateSelected) {
              delete values.fromDate
              delete values.toDate
              delete values.isDateSelected
            }

            let params = Object.keys(values).reduce((a, b) => {
              if (values[b]) {
                return [...a, `${b}=${values[b]}`]
              }
              return a
            }, [] as any)

            await getAccountPayable(params, 0)
            setModalSearch(false)
          }}
        />
      </FloatingForm>

      <Modal display={addPaymentModal} onClose={() => setAddPaymentModal(false)}>
        <AddPaymentForm onClose={() => {
            setAddPaymentModal(false)
        }} onSubmit={onPostIncomeFromAccountPayable}
        accountReceivables={accountPayableToAdd}
        />
      </Modal>

      <Modal
        display={!!errorMessage}
        onClose={() => {
          setErrorMessage('')
        }}
      >
        <div className="text-base dark:text-primary-200 text-primary-1300">
          {errorMessage}
        </div>
      </Modal>

    </motion.div>
  )
}

interface IBillToPay {
  setHeaderActions: React.Dispatch<
    React.SetStateAction<HeaderProps['headerActions'] | null | undefined>
  >
}

export default BillToPay
